'use client';

import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { createClient } from '../lib/supabase/client';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Single stable client instance — never recreated
  const supabase = useMemo(() => createClient(), []);

  // Track last session user id to avoid redundant state updates
  const lastSessionUserId = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    let mounted = true;

    // Get initial session synchronously from storage — avoids an extra network call
    // that onAuthStateChange INITIAL_SESSION would otherwise trigger
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      if (!mounted) return;
      lastSessionUserId.current = initialSession?.user?.id ?? null;
      setSession(initialSession);
      setUser(initialSession?.user ?? null);
      setLoading(false);
    });

    // Listen for subsequent auth changes — skip events that don't change the user
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!mounted) return;

      // TOKEN_REFRESHED: only update session object, don't trigger full re-render cascade
      if (event === 'TOKEN_REFRESHED') {
        setSession(newSession);
        return;
      }

      // INITIAL_SESSION: already handled by getSession() above — skip to avoid double update
      if (event === 'INITIAL_SESSION') {
        return;
      }

      const newUserId = newSession?.user?.id ?? null;

      // Only update state if the user actually changed (SIGNED_IN / SIGNED_OUT)
      if (newUserId !== lastSessionUserId.current) {
        lastSessionUserId.current = newUserId;
        setSession(newSession);
        setUser(newSession?.user ?? null);
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [supabase]);

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: (metadata as any)?.fullName || '',
          avatar_url: (metadata as any)?.avatarUrl || '',
          phone: (metadata as any)?.phone || '',
          instagram: (metadata as any)?.instagram || '',
          referred_by: (metadata as any)?.referredBy || '',
        },
      },
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In — simple and direct, no extra session checks
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    return data;
  };

  // Session validation — uses in-memory session to avoid extra API calls
  const validateSession = useCallback(
    async (): Promise<{ valid: boolean; userId: string | null; error?: string }> => {
      try {
        if (session) {
          const expiresAt = session.expires_at;
          const nowSeconds = Math.floor(Date.now() / 1000);

          // Only refresh if within 60 seconds of expiry
          if (expiresAt && expiresAt < nowSeconds + 60) {
            const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
            if (refreshError || !refreshData?.session) {
              return { valid: false, userId: null, error: 'Session expired. Please log in again.' };
            }
            return { valid: true, userId: refreshData.session.user.id };
          }

          return { valid: true, userId: session.user.id };
        }

        return { valid: false, userId: null, error: 'No active session found.' };
      } catch (err: any) {
        return { valid: false, userId: null, error: err?.message || 'Session validation failed.' };
      }
    },
    [supabase, session]
  );

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  // Get Current User
  const getCurrentUser = async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    validateSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
